import { Card, Chip, EmptyState, Link, Skeleton } from "@heroui/react";
import { useMemo } from "react";
import { useParams } from "react-router";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ConversationViewer } from "@/src/components/ConversationViewer";
import { DetailSection } from "@/src/components/DetailSection";
import { EventStatusChip } from "@/src/components/EventStatusChip";
import { useSetBreadcrumbLeaf } from "@/src/contexts/BreadcrumbContext";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { EventActions } from "@/src/pages/EventDetail/components/EventActions";
import { RosterList } from "@/src/pages/EventDetail/components/RosterList";
import { useEvent } from "@/src/pages/EventDetail/hooks/useEvent";
import { ROUTES } from "@/src/routes/paths";

/**
 * Fiche d'un événement (`/events/:eventId`).
 *
 * Une seule requête — `GET /moderation/events/{id}` — pour tout :
 * l'organisateur, les participants validés, les postulants, les catégories,
 * l'adresse **non masquée**, les coordonnées, et l'identifiant du fil de
 * discussion.
 *
 * ⚠️ **La route publique `GET /event/{id}` ne conviendrait pas** : elle masque
 * l'adresse d'un événement privé même pour un administrateur — le serveur n'y
 * fait aucune exception de rôle, et c'est délibéré de sa part.
 *
 * ⚠️ **Le fil de discussion se lit maintenant depuis ici**, par
 * `GET /moderation/conversations/{id}` — la route d'administration livrée avec
 * ce lot. `GET /conversation/{id}` exige toujours d'en être membre et n'est
 * donc jamais appelée : la fenêtre passe par `ConversationViewer`, partagé avec
 * la fiche d'un compte. **Chaque ouverture est journalisée côté serveur**, d'où
 * un chargement au clic et non au rendu.
 */
export function EventDetail() {
  const { eventId } = useParams();
  const t = useTranslations("events");
  const tm = useTranslations("moderation");
  const tc = useTranslations("conversations");
  const { locale } = useLocale();

  // `Number(undefined)` vaut `NaN`, que le serveur refuserait en 400. Une URL
  // sans nombre est traitée ici comme un événement introuvable, ce qu'elle est.
  const parsed = Number(eventId);
  const isValidId = Number.isInteger(parsed) && parsed > 0;

  const { event, isLoading, isMissing, isError } = useEvent(
    isValidId ? parsed : 0,
  );

  // « Événements > Tournoi de printemps » : l'en-tête ne peut pas résoudre
  // l'identifiant de l'URL, seul cet écran connaît le nom.
  useSetBreadcrumbLeaf(event?.name);

  /*
    Les deux effectifs sont marqués par des données différentes — `winner` ici,
    `ghosted` là — et `RosterList` n'en connaît qu'une, `isFlagged`. La
    conversion est mémoïsée pour ne pas reconstruire les tableaux à chaque
    rendu, et se fait **avant les retours anticipés** : un hook n'a pas le droit
    d'être appelé sous condition.
  */
  const players = useMemo(
    () =>
      (event?.participants ?? []).map((participant) => ({
        armyLabel: participant.gameArmyName ?? participant.gameArmy,
        avatarUrl: participant.avatarUrl,
        id: participant.id,
        isFlagged: participant.winner === true,
        username: participant.username,
      })),
    [event?.participants],
  );

  const applicants = useMemo(
    () =>
      (event?.pendingParticipants ?? []).map((applicant) => ({
        armyLabel: applicant.gameArmyName ?? applicant.gameArmy,
        avatarUrl: applicant.avatarUrl,
        id: applicant.id,
        isFlagged: applicant.ghosted === true,
        username: applicant.username,
      })),
    [event?.pendingParticipants],
  );

  if (!isValidId || isMissing || (isError && !event)) {
    return (
      <Card className="mx-auto w-full max-w-4xl p-10">
        <Card.Content>
          <EmptyState className="flex flex-col items-center gap-2 text-center">
            <p className="text-base font-medium">{t("missing-title")}</p>
            <p className="max-w-md text-pretty text-muted">
              {t("missing-description")}
            </p>
            <Link className="pt-2 text-sm" href={ROUTES.events}>
              {t("back-to-list")}
            </Link>
          </EmptyState>
        </Card.Content>
      </Card>
    );
  }

  if (isLoading || !event) {
    return (
      <div aria-hidden className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  const scheduled =
    event.date === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date(event.date));

  const created = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(event.createdAt));

  // ⚠️ `winner` et `ghosted` sont **optionnels** dans le contrat depuis que les
  // deux effectifs partagent une seule forme : `undefined` s'y lit « non », et
  // non « on ne sait pas ».
  const winners = event.participants.filter(
    (participant) => participant.winner === true,
  ).length;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold tracking-tight text-pretty">
              {event.name}
            </h2>

            <EventStatusChip status={event.status} />

            {/* ⚠️ **Retiré n'est pas fermé** : un événement clos reste visible
                de tous, un événement désapprouvé n'est visible que d'ici. Les
                deux pastilles coexistent donc, elles ne se remplacent pas. */}
            {event.unapprovedAt ? (
              <Chip color="danger" size="sm">
                {tm("event-state-hidden")}
              </Chip>
            ) : null}

            {/* La finalité n'existe que sur un événement terminé sans
                vainqueur : elle explique une fiche où personne n'a gagné. */}
            {event.finality ? (
              <Chip size="sm" variant="secondary">
                {t(
                  event.finality === "equality"
                    ? "finality-equality"
                    : "finality-abandoned",
                )}
              </Chip>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <span>{t("organised-by")}</span>
            <AccountBadge account={event.creatorProfile} isLinked />
          </div>

          <p className="text-sm text-muted">
            {scheduled ?? t("no-date")}
            {" · "}
            {t("created-on", { date: created })}
          </p>

          {event.description ? (
            <p className="text-sm text-pretty whitespace-pre-wrap">
              {event.description}
            </p>
          ) : (
            <p className="text-sm text-muted italic">{t("no-description")}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <Chip size="sm" variant="secondary">
              {t(SUBTYPE_KEY[event.subtype])}
            </Chip>

            {event.level ? (
              <Chip size="sm" variant="secondary">
                {t(LEVEL_KEY[event.level])}
              </Chip>
            ) : null}

            {event.points === null ? null : (
              <Chip size="sm" variant="secondary">
                {t("points", { count: event.points })}
              </Chip>
            )}

            {event.categories.map((category) => (
              <Chip key={category.id} size="sm">
                {category.name}
              </Chip>
            ))}
          </div>
        </div>

        {/*
          Le total demandé, décomposé : ce qui compte sur une fiche d'événement
          n'est pas « combien de monde » mais « combien de places tenues, et
          combien de dossiers en attente ».
        */}
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Counter label={t("counter-places")} value={event.places} />
          <Counter
            label={t("counter-participants")}
            value={event.participants.length}
          />
          <Counter
            isAlarming={event.pendingParticipants.length > 0}
            label={t("counter-pending")}
            value={event.pendingParticipants.length}
          />
          <Counter label={t("counter-winners")} value={winners} />
          <Counter
            label={t("counter-total")}
            value={event.participants.length + event.pendingParticipants.length}
          />
        </ul>
      </Card>

      {/*
        Les actions **avant** le dossier : on ouvre la fiche d'un événement
        signalé pour décider, pas pour lire son adresse.
      */}
      <EventActions event={event} />

      <DetailSection
        emptyLabel={t("location-unknown")}
        isEmpty={event.locationLabel === null && event.latitude === null}
        title={t("location")}
      >
        <div className="flex flex-col gap-2">
          {/* `select-all` : une adresse se recopie d'un double-clic, elle n'est
              pas noyée dans une phrase. */}
          {event.locationLabel ? (
            <p className="text-sm select-all">{event.locationLabel}</p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Chip
              color={event.isLocationPublic ? "default" : "warning"}
              size="sm"
            >
              {t(
                event.isLocationPublic ? "location-public" : "location-hidden",
              )}
            </Chip>

            {/* ⚠️ **Les coordonnées ne sortent que par l'administration** — pas
                même par `GET /event/{id}`. Elles se recopient telles quelles
                dans une carte, ce qui reste plus utile qu'une carte encastrée
                qu'il faudrait charger depuis un tiers. */}
            {event.latitude === null || event.longitude === null ? null : (
              <span className="text-xs text-muted tabular-nums select-all">
                {t("coordinates", {
                  lat: event.latitude,
                  lng: event.longitude,
                })}
              </span>
            )}
          </div>

          {/* Une adresse privée reste privée partout ailleurs : l'écran le dit,
              sans quoi on pourrait la recopier en croyant qu'elle est
              publique. */}
          {event.isLocationPublic ? null : (
            <p className="text-xs text-pretty text-muted">
              {t("location-hidden-note")}
            </p>
          )}
        </div>
      </DetailSection>

      <DetailSection
        count={event.participants.length}
        emptyLabel={t("participants-empty")}
        isEmpty={event.participants.length === 0}
        title={t("participants")}
      >
        <RosterList
          flagColor="accent"
          flagLabel={t("winner")}
          members={players}
        />
      </DetailSection>

      <DetailSection
        count={event.pendingParticipants.length}
        emptyLabel={t("applicants-empty")}
        isEmpty={event.pendingParticipants.length === 0}
        title={t("applicants")}
      >
        <RosterList
          flagColor="warning"
          flagLabel={t("ghosted")}
          members={applicants}
        />
      </DetailSection>

      <DetailSection
        emptyLabel={t("conversation-none")}
        isEmpty={event.conversationId === null}
        title={t("conversation")}
      >
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm">
            {t("conversation-exists", { id: event.conversationId ?? 0 })}
          </p>

          {/*
            ⚠️ **Le serveur journalise chaque ouverture**, avec l'administrateur
            qui a lu. C'est ce qui borne l'accès consenti par ce lot — d'où un
            chargement au clic : monter la fenêtre au rendu de la fiche
            inscrirait au registre une consultation qui n'a pas eu lieu.
          */}
          {event.conversationId === null ? null : (
            <ConversationViewer
              conversationId={event.conversationId}
              label={tc("open")}
              title={tc("event-title")}
            />
          )}
        </div>
      </DetailSection>
    </div>
  );
}

const SUBTYPE_KEY = {
  solo: "subtype-solo",
  duo: "subtype-duo",
  team: "subtype-team",
} as const;

const LEVEL_KEY = {
  beginner: "level-beginner",
  intermediate: "level-intermediate",
  advanced: "level-advanced",
  pro: "level-pro",
} as const;

function Counter({
  label,
  value,
  isAlarming = false,
}: {
  label: string;
  value: number;
  isAlarming?: boolean;
}) {
  return (
    <li className="flex flex-col items-center rounded-lg bg-default px-2 py-3 text-default-foreground">
      <span
        className={`text-lg font-semibold tabular-nums ${
          isAlarming ? "text-danger" : ""
        }`}
      >
        {value}
      </span>
      <span className="text-center text-xs text-muted">{label}</span>
    </li>
  );
}
